import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

export default function PatientLogin() {
  const { slug } = useParams();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/tenants/by-slug/${slug}`).then(({ data }) => setTenant(data)).catch(() => {});
  }, [slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(email, password, slug);
      if (u.role === "patient") navigate(`/c/${slug}/dashboard`);
      else navigate("/admin");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Login failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    sessionStorage.setItem("mr_pending_tenant_slug", slug);
    const redirectUrl = window.location.origin + `/c/${slug}/dashboard`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-mr-bg grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1586773860418-d37222d8fce3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob3NwaXRhbCUyMGJ1aWxkaW5nJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzgxNDQyNTY5fDA&ixlib=rb-4.1.0&q=85"
          alt="hospital" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0F8964]/85 via-[#0F8964]/40 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <p className="label-eyebrow text-white/75">{tenant?.name || "Clinic"}</p>
          <h2 className="font-display text-3xl font-semibold leading-tight mt-2 max-w-md">Your health, beautifully rewarded.</h2>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md">
          <Link to={`/c/${slug}`} className="inline-flex items-center gap-2 mb-10" data-testid="login-back-link">
            <div className="h-8 w-8 rounded-lg bg-mr-primary text-white flex items-center justify-center">
              <Sparkles className="h-4 w-4" />
            </div>
            <span className="font-display font-semibold text-mr-primary">{tenant?.name || "MedRewards"}</span>
          </Link>

          <h1 className="font-display text-3xl font-semibold text-mr-primary">Welcome back</h1>
          <p className="text-mr-secondary mt-2">Sign in to view your points and rewards.</p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4">
            <div>
              <Label className="text-mr-primary">Email</Label>
              <Input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-2 rounded-xl bg-white border-mr h-11" />
            </div>
            <div>
              <Label className="text-mr-primary">Password</Label>
              <Input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-2 rounded-xl bg-white border-mr h-11" />
            </div>
            <Button data-testid="login-submit-btn" type="submit" disabled={submitting} className="w-full h-11 rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]">
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3">
            <Separator className="flex-1" />
            <span className="label-eyebrow text-mr-secondary">or</span>
            <Separator className="flex-1" />
          </div>

          <Button data-testid="login-google-btn" onClick={handleGoogle} variant="outline" className="w-full h-11 rounded-full border-mr">
            <img alt="Google" className="h-4 w-4 mr-2" src="https://www.google.com/favicon.ico" />
            Continue with Google
          </Button>

          <p className="mt-6 text-sm text-mr-secondary text-center">
            New patient? <Link data-testid="login-register-link" to={`/c/${slug}/register`} className="text-mr-primary font-medium underline underline-offset-4">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
